import { MenuItem, Select, SelectChangeEvent, Stack, Switch, Typography } from '@mui/material'
import { debounce } from 'lodash'
import { ChangeEvent, FC, useCallback, useEffect, useState } from 'react'
import { useProfileStore } from '../../../../store/storeProfile'
import { useRequestsStore } from '../../../../store/storeRequests'
import { useWrapperStore } from '../../../../store/storeWrapper'
import { RequestSettingVoiceAnswer } from '../../../../types/requests'
import { SettingsSlider } from './SettingsSlider'

const openaiVoices = [
    { value: 'alloy', label: 'Нейтральный' },
    { value: 'echo', label: 'Уверенный' },
    { value: 'fable', label: 'Деловой' },
    { value: 'onyx', label: 'Серьезный' },
    { value: 'nova', label: 'Добрый' },
    { value: 'shimmer', label: 'Чувственный' },
]

const sliderValues = {
    min: 0.25,
    max: 4,
}

export const VoiceAnswerSettings: FC = () => {
    const { updateRequest, requestDetail, isLoadingUpdateRequest, setRequestSettingVoiceAnswer } =
        useRequestsStore((state) => state)
    const { modelDetail } = useWrapperStore((state) => state)
    const { isFullAccess } = useProfileStore()
    const [isDirty, setIsDirty] = useState(false)
    const [state, setState] = useState<RequestSettingVoiceAnswer>({
        voiceAnswer: requestDetail?.voice_answer ?? false,
        speechVoice: requestDetail?.speech_voice ?? 'alloy',
        voiceSpeed: requestDetail?.voice_speed ?? 1,
    })

    const delayedUpdateRequestDetail = useCallback(
        debounce(async (newState: RequestSettingVoiceAnswer) => {
            setRequestSettingVoiceAnswer(newState)

            if (requestDetail?.id) {
                await updateRequest(
                    { threadId: requestDetail.id },
                    {
                        voice_answer: newState.voiceAnswer,
                        voice_speed: newState.voiceSpeed,
                        speech_voice: newState.speechVoice,
                    },
                )
            }
        }, 400),
        [],
    )

    const handelChangeEnabled = (event: ChangeEvent<HTMLInputElement>) => {
        setIsDirty(true)
        setState((prev) => ({ ...prev, voiceAnswer: event.target.checked }))
    }

    const handelChangeVoiceType = (event: SelectChangeEvent<string>) => {
        setIsDirty(true)
        setState((prev) => ({ ...prev, speechVoice: event.target.value }))
    }

    const handelChangeSpeechSpeed = (_: unknown, value: number | number[]) => {
        setIsDirty(true)

        if (!Array.isArray(value)) {
            setState((prev) => ({ ...prev, voiceSpeed: value }))
        }
    }

    useEffect(() => {
        if (isDirty) {
            delayedUpdateRequestDetail(state)
        }

        return () => {
            delayedUpdateRequestDetail.cancel()
        }
    }, [isDirty, state, delayedUpdateRequestDetail])

    return (
        <Stack gap={3}>
            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h5">Включить</Typography>
                <Switch
                    size="small"
                    checked={state.voiceAnswer}
                    onChange={handelChangeEnabled}
                    disabled={isLoadingUpdateRequest || !isFullAccess}
                />
            </Stack>
            {state.voiceAnswer && (
                <>
                    <Stack gap={1}>
                        <Typography variant="h5">Выбор голоса</Typography>
                        <Select
                            displayEmpty
                            MenuProps={{
                                anchorOrigin: {
                                    vertical: 'top',
                                    horizontal: 'center',
                                },
                                transformOrigin: {
                                    vertical: 'top',
                                    horizontal: 'left',
                                },
                            }}
                            value={state.speechVoice}
                            onChange={handelChangeVoiceType}
                            disabled={isLoadingUpdateRequest}
                            color="secondary">
                            {openaiVoices.map((voice) => {
                                return (
                                    <MenuItem
                                        value={voice.value}
                                        key={voice.value}
                                        sx={{
                                            borderBottom: '1px solid #F4F4F6',
                                            p: '10px 16px',
                                        }}>
                                        <Typography variant="h6">{voice.label}</Typography>
                                    </MenuItem>
                                )
                            })}
                        </Select>
                    </Stack>
                    <Stack gap={1}>
                        <Typography variant="h5">Скорость речи</Typography>
                        <SettingsSlider
                            marks={[
                                {
                                    value: sliderValues.min,
                                    label: sliderValues.min,
                                },
                                {
                                    value: sliderValues.max,
                                    label: sliderValues.max,
                                },
                            ]}
                            value={state.voiceSpeed}
                            onChange={handelChangeSpeechSpeed}
                            step={sliderValues.min}
                            min={sliderValues.min}
                            max={sliderValues.max}
                        />
                    </Stack>
                </>
            )}
        </Stack>
    )
}
